import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Pagination, Stack } from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";

import PageContainer from "../layouts/PageContainer";
import PageToolbar from "../components/common/PageToolbar";
import AppButton from "../components/buttons/AppButton";
import ExpenseCard from "../components/expenses/ExpenseCard";
import CreateExpenseModal from "../components/expenses/CreateExpenseModal";
import ViewExpenseModal from "../components/expenses/ViewExpenseModal";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PageError from "../components/common/PageError";

import useDebounce from "../hooks/useDebounce";
import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getExpenses,
  getExpenseById,
  resetExpenseServerStatus,
} from "../redux/actions/expenseActions";
import { ExpenseSummaryDto } from "../shared/dtos/expense/ExpenseSummaryDto";

const ExpensesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- Redux State ---
  const {
    items: expenses,
    pagination,
    loadingFetchAll: loadingExpenses,
    errorFetchAll: errorLoadingExpenses,
    serverStatus,
    loadingCreate,
    errorCreate,
    item: currentExpense,
    loadingFetchOne: loadingExpense,
    errorFetchOne: errorLoadingExpense,
    // loadingDelete, // Used for voiding if implemented in your slice
  } = useAppSelector((state) => state.expense);

  // --- Local State ---
  const [page, setPage] = useState<number>(1);
  const limit = 12;
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);
  const [isViewOpen, setIsViewOpen] = useState<boolean>(false);

  // --- Permissions ---
  const canCreate = hasPermission("expense:create");

  // --- Data Fetching ---
  const fetchExpenses = useCallback((): void => {
    dispatch(getExpenses({ page, size: limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  // --- Handle Server Feedback (Modals) ---
  useEffect(() => {
    if (!serverStatus) return;
    const { operation, status } = serverStatus;

    if (operation === "create" && (status === 201 || status === 200)) {
      setIsCreateOpen(false);
    }

    dispatch(resetExpenseServerStatus());
  }, [serverStatus, dispatch, fetchExpenses]);

  // --- Handlers ---
  const handleOpenView = (id: string): void => {
    dispatch(getExpenseById(id));
    setIsViewOpen(true);
  };

  const renderContent = (): React.JSX.Element => {
    if (loadingExpenses) return <AppCircularProgress />;

    if (errorLoadingExpenses) {
      return <PageError error={errorLoadingExpenses} onRetry={fetchExpenses} />;
    }

    if (!expenses || expenses.length === 0) {
      return (
        <Box sx={{ textAlign: "center", mt: 5 }}>
          <Typography color="text.secondary">
            No expense records found.
          </Typography>
        </Box>
      );
    }

    return (
      <>
        {/* CSS Grid for perfect Card alignment */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 3,
          }}
        >
          {expenses.map((e) => (
            <ExpenseCard
              key={e.id}
              expense={e as unknown as ExpenseSummaryDto}
              onView={(ev) => {
                ev.stopPropagation();
                handleOpenView(e.id);
              }}
            />
          ))}
        </Box>

        {pagination?.totalPages && pagination.totalPages > 1 && (
          <Stack sx={{ alignItems: "center", mt: 4 }}>
            <Pagination
              count={pagination.totalPages}
              page={page}
              onChange={(_, v) => setPage(v)}
              color="primary"
            />
          </Stack>
        )}
      </>
    );
  };

  return (
    <PageContainer
      title="Business Expenses"
      subtitle="Track operational costs, utilities, and daily payouts."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<ReceiptLongIcon />}
            onClick={() => setIsCreateOpen(true)}
          >
            Record Expense
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => {
          setSearchTerm(e.target.value);
          setPage(1);
        }}
        searchPlaceholder="Search by description or category..."
      />

      {renderContent()}

      {/* --- MODALS --- */}

      {isCreateOpen && (
        <CreateExpenseModal
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          loading={loadingCreate}
          error={errorCreate}
        />
      )}

      {isViewOpen && (
        <ViewExpenseModal
          open={isViewOpen}
          onClose={() => setIsViewOpen(false)}
          expense={currentExpense}
          loading={loadingExpense}
          error={errorLoadingExpense}
        />
      )}
    </PageContainer>
  );
};

export default ExpensesPage;
